/**
 * @fileoverview Components for the Publisher Setup Page as a part of the Listings Launch flow.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */


import AjaxSource from '/js/yext/externalaccounts/ajaxsource';
import {ADD_LINKED_ACCOUNT_MODAL_TITLE,
  ADD_LINKED_ACCOUNT_MODAL_CTA_CANCEL,
  ADD_LINKED_ACCOUNT_MODAL_CONTENT,
  UNKNOWN_ERROR} from '/js/yext/externalaccounts/listingslaunch/copy';
import ExternalAccountsTable
  from '/js/yext/externalaccounts/listingslaunch/externalaccountstable';
import LinkExternalAccount
  from '/js/yext/externalaccounts/listingslaunch/linkexternalaccount';
import {ExternalAccountsContext}
  from '/js/yext/externalaccounts/shared/listingslaunch/externalaccountscontext';
import {Button} from '/ui/components/button/button';
import {ContentSkeleton} from '/ui/components/contentskeleton/contentskeleton';
import {SmallDialog} from '/ui/components/modal/dialog';
import * as toast from '/ui/components/toast/toast';

/**
 * Publisher Setup instructions and linking account functionality
 * @param {{
 *   publisherId: number,
 *   canLinkOrgAccount?: boolean,
 *   showSearch?: boolean,
 *   skipSetup?: boolean,
 *   enableCallout?: boolean,
 * }} props
 * @return {React.ReactNode}
 */
export default function ExternalAccounts({
  publisherId,
  canLinkOrgAccount = false,
  showSearch = false,
  skipSetup=false,
  enableCallout=true,
}) {
  const {
    publisherIdToUserLinkedExternalAccounts,
    setPublisherIdToUserLinkedExternalAccounts,
  } = React.useContext(ExternalAccountsContext);

  const [publisher, setPublisher] = React.useState({});
  const [publisherLoading, setPublisherLoading] =
    React.useState(true);
  const [hasManageLinkedAccountPermission, setHasManageLinkedAccountPermission] =
    React.useState(true);
  const [loadedExternalAccounts, setLoadedExternalAccounts] =
    React.useState([]);
  const [externalAccountsLoading, setExternalAccountsLoading] =
    React.useState(false);
  const [externalAccountsLoaded, setExternalAccountsLoaded] =
    React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [ajaxsource] = React.useState(new AjaxSource());

  React.useEffect(() => {
    const abortController = new AbortController();
    ajaxsource.fetchPublisherInfo(publisherId, abortController.signal)
      .then(publisher => setPublisher(publisher))
      .catch(unknownError)
      .finally(() => setPublisherLoading(false));
    return () => abortController.abort();
  }, [publisherId, ajaxsource]);

  React.useEffect(() => {
    const abortController = new AbortController();
    // NOTE(jmay): the list userLinkedExternalAccountIds is required for the
    // following scenario: if a user links a new account in the
    // listinglaunch flow, continues in the flow, then goes back to the
    // external account select page, the new linked account will be duplicated
    // in the external accounts list as we fetch it as well as append it from
    // userLinkedExternalAccounts. this list filters out user-added linked
    // accounts from the fetched accounts to prevent this duplication.
    const userLinkedExternalAccountIds =
        publisherIdToUserLinkedExternalAccounts.get(publisherId)?.map(
          userLinkedExternalAccount => userLinkedExternalAccount.id) ?? [];
    if (!externalAccountsLoaded) {
      ajaxsource.fetchExternalAccounts(publisherId, abortController.signal)
        .then(response => {
          setLoadedExternalAccounts(response.externalAccounts.filter(
            externalAccount => !userLinkedExternalAccountIds.includes(externalAccount.id)));
          setExternalAccountsLoaded(true);
          setHasManageLinkedAccountPermission(response.hasManageLinkedAccountPermission);
        })
        .catch(unknownError)
        .finally(() => setExternalAccountsLoading(false));
    }
    return () => abortController.abort();
  }, [
    publisherId,
    ajaxsource,
    externalAccountsLoaded,
    publisherIdToUserLinkedExternalAccounts]);

  // use callback is used here to remove the proper event listener in handleHideDialog
  // otherwise the page continues to listen for messages even when the dialog is closed
  const handleMessage = React.useCallback(event => {
    function isPendoMessage(message) {
      return 'type' in message && message['type'].includes('pendo');
    }

    function isReactMessage(message) {
      return 'source' in message && message.source.includes('react');
    }

    function isYextEvent() {
      return event.origin.includes('yext') || event.origin.includes(
        'localhost');
    }

    function stopListeningForResponse() {
      window.removeEventListener('message', handleMessage);
      setShowDialog(false);
    }

    function handleSaveExternalAccountResponse(saveExternalAccountResponse) {
      const linkedExternalAccount = saveExternalAccountResponse['externalAccountInformation'];
      if (saveExternalAccountResponse.status === 'SUCCESS') {
        const seenExternalAccountIds =
          new Set(
            [
              ...loadedExternalAccounts,
              ...publisherIdToUserLinkedExternalAccounts.get(publisherId) || [],
            ].map(externalAccount => externalAccount.id));
        if (!seenExternalAccountIds.has(linkedExternalAccount.id)) {
          const userLinkedExternalAccounts =
              publisherIdToUserLinkedExternalAccounts.get(publisherId) || [];
          userLinkedExternalAccounts.push(linkedExternalAccount);
          setPublisherIdToUserLinkedExternalAccounts(prev =>
            new Map(prev).set(publisherId, userLinkedExternalAccounts));
        } else if (linkedExternalAccount.isValid) {
          setLoadedExternalAccounts(
            prev => {
              const newLoadedExternalAccounts =
                prev.filter(
                  externalAccount =>
                    externalAccount.id !== linkedExternalAccount.id);
              newLoadedExternalAccounts.push(linkedExternalAccount);
              return newLoadedExternalAccounts;
            });
        }
      } else if (saveExternalAccountResponse.status === 'ERROR') {
        toast.showErrorToast(saveExternalAccountResponse.errorMessage);
      }
      stopListeningForResponse();
    }

    if (isYextEvent()) {
      const message = event.data;
      if ('status' in message) {
        handleSaveExternalAccountResponse(message);
      } else if (isPendoMessage(message) || isReactMessage(message)) {
        // ignore expected pendo / react messages
        return;
      } else {
        // We'd like to log unknown errors in sentry
        unknownError(false);
        stopListeningForResponse();
        throw event.data;
      }
    }
  }, [
    publisherId,
    publisherIdToUserLinkedExternalAccounts,
    setPublisherIdToUserLinkedExternalAccounts,
    loadedExternalAccounts]);

  const handleHideDialog = React.useCallback(() => {
    window.removeEventListener('message', handleMessage);
    setShowDialog(false);
  }, [handleMessage]);

  const externalAccounts = React.useMemo(() => [
    ...publisherIdToUserLinkedExternalAccounts.get(publisherId) || [],
    ...loadedExternalAccounts,
  ], [publisherIdToUserLinkedExternalAccounts, loadedExternalAccounts, publisherId]);

  if (publisherLoading || externalAccountsLoading) {
    return (
      <div>
        <ContentSkeleton type="box" height={192}/>
        <br/>
        <ContentSkeleton numRows={5}/>
      </div>
    );
  }

  const openPopup = url => {
    const windowOptions = 'resizable=yes,scrollbars=yes,width=640,height=500';
    const popup = window.open('', '_blank', windowOptions);
    popup.location = url;
  };

  const handleAddNewAccount = url => {
    window.addEventListener('message', handleMessage);
    setShowDialog(true);
    openPopup(url);
  };

  const handleAddOrgAccount = () => {
    ajaxsource.saveYextOrgAccount(publisherId, new AbortController().signal)
      .then(result => {
        if (result['status'] === 'SUCCESS') {
          const orgAccount = result['externalAccountInformation'];
          const seenExternalAccountIds = [
            ...loadedExternalAccounts,
            ...(publisherIdToUserLinkedExternalAccounts.get(publisherId) || []),
          ].map(account => account.id);

          if (new Set(seenExternalAccountIds).has(orgAccount.id)) {
            const publisherAccounts = publisherIdToUserLinkedExternalAccounts.get(publisherId) || [];
            publisherAccounts.push(orgAccount);

            setPublisherIdToUserLinkedExternalAccounts(prev =>
              new Map(prev).set(publisherId, publisherAccounts));
          } else {
            setLoadedExternalAccounts(prev => {
              const loadedAccounts = prev.filter(account => account.id !== orgAccount.id);
              loadedAccounts.push(orgAccount);

              return loadedAccounts;
            });
          }
        } else {
          toast.showErrorToast(result['errorMessage']);
        }
      })
      .catch(unknownError);
  };

  const linkAccountCallToAction = {
    primary: <Button
      pendoId={'listings-launch-flow-linking-cancel-linking'}
      onClick={handleHideDialog}>{ADD_LINKED_ACCOUNT_MODAL_CTA_CANCEL}
    </Button>,
  };

  return (
    <div>
      {externalAccounts.length === 0
            ? <LinkExternalAccount publisher={publisher}
              handleAddNewAccount={handleAddNewAccount}
              handleAddOrgAccount={handleAddOrgAccount}
              openPopup={openPopup}
              hasManageLinkedAccountPermission={hasManageLinkedAccountPermission}
              canLinkOrgAccount={canLinkOrgAccount}
              skipSetup={skipSetup}
              enableCallout={enableCallout}
            />
            : <ExternalAccountsTable publisher={publisher}
              externalAccounts={externalAccounts}
              showSearch={!!showSearch}
              skipSetup={!!skipSetup}
              handleAddNewAccount={handleAddNewAccount}/>}
      <SmallDialog
        title={ADD_LINKED_ACCOUNT_MODAL_TITLE}
        show={showDialog}
        callToAction={linkAccountCallToAction}
        onHideDialog={handleHideDialog}
      >
        {ADD_LINKED_ACCOUNT_MODAL_CONTENT}
      </SmallDialog>
    </div>
  );
}

function unknownError(isAbort) {
  if (!isAbort) {
    toast.showErrorToast(<span>{UNKNOWN_ERROR}</span>);
  }
}
